import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useFavoriteWorkplacesFlag } from "@src/appV2/FeatureFlags/useFavoriteWorkplacesFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

// Request schema for the entitlements API
const getFavoriteWorkplaceEntitlementRequestSchema = z.object({
  filter: z.object({
    workerId: z.string(),
  }),
});

// Response schema based on JSON:API specification
const favoriteWorkplaceEntitlementDataSchema = z.object({
  id: z.string(),
  type: z.literal("favorite-workplace-entitlement"),
  attributes: z.object({
    workerId: z.string(),
  }),
});

const getFavoriteWorkplaceEntitlementResponseSchema = z.object({
  data: z.array(favoriteWorkplaceEntitlementDataSchema),
});

export type GetFavoriteWorkplaceEntitlementRequest = z.infer<
  typeof getFavoriteWorkplaceEntitlementRequestSchema
>;
export type GetFavoriteWorkplaceEntitlementResponse = z.infer<
  typeof getFavoriteWorkplaceEntitlementResponseSchema
>;
export type FavoriteWorkplaceEntitlementData = z.infer<
  typeof favoriteWorkplaceEntitlementDataSchema
>;

export const FAVORITE_WORKPLACE_ENTITLEMENTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/favorite-workplaces/entitlements`;

// FIXME: Automatically disabled during ESLint config update
export interface UseGetFavoriteWorkplaceEntitlementOptions extends UseGetQueryOptions<GetFavoriteWorkplaceEntitlementResponse> {}

function useGetFavoriteWorkplaceEntitlementQuery(
  params: GetFavoriteWorkplaceEntitlementRequest,
  options: UseGetFavoriteWorkplaceEntitlementOptions = {}
): UseQueryResult<GetFavoriteWorkplaceEntitlementResponse> {
  return useGetQuery({
    url: FAVORITE_WORKPLACE_ENTITLEMENTS_URL,
    queryParams: params,
    requestSchema: getFavoriteWorkplaceEntitlementRequestSchema,
    responseSchema: getFavoriteWorkplaceEntitlementResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FAVORITE_WORKPLACES_FAILURE,
    },
    ...options,
  });
}

/**
 * Hook to get favorite workplace entitlement for the current worker.
 * This replaces the useFavoriteWorkplacesFlag hook by checking entitlements via API.
 */
export function useGetFavoriteWorkplaceEntitlement(
  options: UseGetFavoriteWorkplaceEntitlementOptions = {}
) {
  const { userId: workerId } = useDefinedWorker();

  const queryResult = useGetFavoriteWorkplaceEntitlementQuery(
    {
      filter: {
        workerId,
      },
    },
    {
      enabled: Boolean(workerId),
      ...options,
    }
  );

  const flag = useFavoriteWorkplacesFlag();

  const { data } = queryResult;

  // Extract entitlement data or use defaults
  const isEntitlementEnabled = data?.data?.[0]?.attributes?.workerId === workerId;

  return {
    isEntitlementEnabled: isEntitlementEnabled && flag.enabled,
  };
}
